/**
 * 워크플로 일괄 등록 — 같은 노드·연결의 워크플로를 이름·속성·변수/파라미터 값만 달리해 여러 개 만든다.
 *
 * 화면(BulkCreateScreen)은 표만 그리고, 판단은 모두 여기서 한다 (테스트: bulkCreate.test.ts).
 *
 *   템플릿(워크플로 하나) → 열(templateColumns): 워크플로 이름·설명·폴더·스케줄 / ${변수} / 노드마다 이름·속성·파라미터
 *   표의 한 행 = 새 워크플로 하나. 칸을 비우지 않고 템플릿 값에서 시작한다 — 고칠 칸만 고친다.
 *   행 → 워크플로(applyRow) → 검증(validateRows) → 등록(화면이 repo 로 저장)
 *
 * CSV 머리글은 **열 키**다 — 언어와 무관하게 다시 불러올 수 있어야 해서 화면 문구가 아니라 키를 쓴다.
 *   name, description, folder, schedule         워크플로
 *   ${YMD}                                       변수 — 값을 주면 노드 설정 안의 ${YMD} 를 그 값으로 바꿔 넣는다
 *   <노드이름>.label                             노드 이름
 *   <노드이름>.<속성키>                          노드 속성 (글자·숫자·예/아니오 값만 — 목록·표 형태 속성은 템플릿 그대로)
 *   <노드이름>.param:<이름>                      노드 파라미터
 * 노드 이름이 겹치면 `<노드이름>#<노드id>` 로 가른다.
 */
import type { Edge, Node } from '@xyflow/react';
import { extractParamRefs } from '../../types/workflowSettings';

export type ColumnKind = 'wf' | 'var' | 'label' | 'cfg' | 'param';
export type ValueType = 'string' | 'number' | 'boolean';

export interface BulkColumn {
  /** CSV 머리글이자 행 값의 키 */
  key: string;
  kind: ColumnKind;
  /** wf: name|description|folder|schedule · var: 변수 이름 · cfg: 설정 키 · param: 파라미터 이름 · label: 'label' */
  field: string;
  nodeId?: string;
  nodeName?: string;
  nodeType?: string;
  valueType: ValueType;
  /** 템플릿의 값 — 새 행은 이 값에서 시작한다 */
  templateValue: string;
}

export interface TemplateDoc {
  name: string;
  description?: string;
  icon?: string;
  folder?: string;
  nodes: Node[];
  edges: Edge[];
  settings?: any;
}

export type BulkRow = Record<string, string>;

export interface BuiltWorkflow {
  name: string;
  description: string;
  folder: string;
  icon?: string;
  nodes: Node[];
  edges: Edge[];
  settings: any;
}

export const WF_FIELDS = ['name', 'description', 'folder', 'schedule'] as const;

/** 속성 열로 내놓지 않는 설정 키 — 화면이 따로 다루거나(이름·파라미터) 내부 표시다 */
const SKIP_CFG = new Set(['label', 'params', 'dataDefinitionId', 'columnsKey', 'columnsChange']);
/** 화면이 스스로 적어 두는 시각 표시(열 목록을 읽은 때 등) — 사람이 고칠 값이 아니다 */
const SKIP_CFG_PATTERN = /(FetchedAt|SyncedAt)$/;

const nodeName = (n: any): string => String(n?.data?.label ?? n?.data?.config?.label ?? n?.id ?? '');

const isScalar = (v: unknown) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

const typeOf = (v: unknown): ValueType => (typeof v === 'number' ? 'number' : typeof v === 'boolean' ? 'boolean' : 'string');

/** 템플릿에서 표의 열을 만든다 — 순서: 워크플로 → 변수 → 노드(캔버스 순서)별 이름·파라미터·속성 */
export function templateColumns(tpl: TemplateDoc): BulkColumn[] {
  const cols: BulkColumn[] = [];
  const wfValue: Record<string, string> = {
    name: tpl.name || '',
    description: tpl.description || '',
    folder: tpl.folder || '',
    schedule: tpl.settings?.schedule ?? '',
  };
  WF_FIELDS.forEach((f) => cols.push({ key: f, kind: 'wf', field: f, valueType: 'string', templateValue: String(wfValue[f] ?? '') }));

  extractParamRefs(tpl.nodes).sort().forEach((v) => cols.push({ key: `\${${v}}`, kind: 'var', field: v, valueType: 'string', templateValue: '' }));

  const names = tpl.nodes.map(nodeName);
  tpl.nodes.forEach((n: any, i) => {
    const base = names[i];
    const nk = names.filter((x) => x === base).length > 1 || !base ? `${base}#${n.id}` : base;
    const common = { nodeId: n.id as string, nodeName: base, nodeType: n.type as string };
    cols.push({ key: `${nk}.label`, kind: 'label', field: 'label', valueType: 'string', templateValue: base, ...common });
    const cfg = n?.data?.config || {};
    (Array.isArray(cfg.params) ? cfg.params : []).forEach((p: any) => {
      if (p && typeof p.name === 'string' && p.name) {
        cols.push({ key: `${nk}.param:${p.name}`, kind: 'param', field: p.name, valueType: 'string', templateValue: String(p.value ?? ''), ...common });
      }
    });
    Object.keys(cfg).forEach((k) => {
      const v = cfg[k];
      if (SKIP_CFG.has(k) || SKIP_CFG_PATTERN.test(k) || k.startsWith('_') || !isScalar(v)) return;
      cols.push({ key: `${nk}.${k}`, kind: 'cfg', field: k, valueType: typeOf(v), templateValue: String(v), ...common });
    });
  });
  return cols;
}

/** 처음부터 표에 보일 열 — 워크플로 이름·설명·폴더, 변수, 노드 파라미터 (속성은 골라서 더한다) */
export function defaultVisibleKeys(cols: BulkColumn[]): string[] {
  return cols.filter((c) => (c.kind === 'wf' && c.field !== 'schedule') || c.kind === 'var' || c.kind === 'param').map((c) => c.key);
}

/** 템플릿 값으로 채운 새 행. 이름은 겹치지 않게 뒤에 번호를 붙인다 */
export function newRow(cols: BulkColumn[], index: number): BulkRow {
  const row: BulkRow = {};
  cols.forEach((c) => { row[c.key] = c.templateValue; });
  if (row.name !== undefined) row.name = `${row.name || 'workflow'}_${index + 1}`;
  return row;
}

const parseBool = (s: string): boolean | null => {
  const v = s.trim().toLowerCase();
  if (['true', 'y', 'yes', '1'].includes(v)) return true;
  if (['false', 'n', 'no', '0'].includes(v)) return false;
  return null;
};

const convert = (c: BulkColumn, raw: string): unknown => {
  if (c.valueType === 'number') return raw.trim() === '' ? undefined : Number(raw);
  if (c.valueType === 'boolean') return parseBool(raw) ?? undefined;
  return raw;
};

/** 문자열 안의 ${이름} 을 값으로 바꾼다 (객체·배열은 안쪽까지) */
function substitute(v: any, vars: Record<string, string>): any {
  if (typeof v === 'string') return v.replace(/\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));
  if (Array.isArray(v)) return v.map((x) => substitute(x, vars));
  if (v && typeof v === 'object') return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, substitute(x, vars)]));
  return v;
}

/** 한 행으로 새 워크플로를 만든다 — 노드·연결은 템플릿 그대로, 고른 칸만 바뀐다 */
export function applyRow(tpl: TemplateDoc, cols: BulkColumn[], row: BulkRow): BuiltWorkflow {
  const nodes: any[] = JSON.parse(JSON.stringify(tpl.nodes || []));
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const vars: Record<string, string> = {};
  cols.forEach((c) => {
    const raw = row[c.key];
    if (raw === undefined) return;
    if (c.kind === 'var') { if (raw !== '') vars[c.field] = raw; return; }
    if (c.kind === 'wf') return;
    const n = byId.get(c.nodeId);
    if (!n) return;
    n.data = { ...(n.data || {}) };
    const cfg = { ...(n.data.config || {}) };
    if (c.kind === 'label') {
      n.data.label = raw;
      if ('label' in cfg) cfg.label = raw;          // 설정에도 이름이 있으면 같이 (속성 일괄수정과 같은 규칙)
    } else if (c.kind === 'param') {
      cfg.params = (Array.isArray(cfg.params) ? cfg.params : []).map((p: any) => (p?.name === c.field ? { ...p, value: raw } : p));
    } else {
      const v = convert(c, raw);
      if (v === undefined) delete cfg[c.field]; else cfg[c.field] = v;
    }
    n.data.config = cfg;
  });
  if (Object.keys(vars).length) {
    nodes.forEach((n) => { if (n.data?.config) n.data.config = substitute(n.data.config, vars); });
  }
  const settings = { ...(tpl.settings || {}) };
  if (row.schedule !== undefined) settings.schedule = row.schedule.trim() || null;
  return {
    name: (row.name ?? tpl.name ?? '').trim(),
    description: row.description ?? tpl.description ?? '',
    folder: (row.folder ?? tpl.folder ?? '').trim(),
    icon: tpl.icon,
    nodes,
    edges: JSON.parse(JSON.stringify(tpl.edges || [])),
    settings,
  };
}

// ---------------------------------------------------------------- 검증

export type IssueCode =
  | 'nameRequired' | 'nameDupRows' | 'nameExists' | 'notNumber' | 'notBoolean' | 'badCron'
  | 'unresolvedVar' | 'unknownConn';

export interface BulkIssue {
  row: number;
  key: string;
  level: 'error' | 'warning';
  code: IssueCode;
  params?: Record<string, string | number>;
}

const CONN_KEYS = /^(conn_id|connection|connId|connection_id)$/;
const cronOk = (s: string) => !s || /^@(hourly|daily|weekly|monthly|yearly|once)$/.test(s) || s.trim().split(/\s+/).length === 5;
const folderKey = (name: string, folder: string) => `${folder.trim()}\u0001${name.trim().toLowerCase()}`;

/**
 * 등록 전에 막을 것(error)과 알릴 것(warning).
 * existing: 이 프로젝트에 이미 있는 워크플로 — 같은 폴더에 같은 이름이면 막는다.
 * connections: 알려진 연결 id — 모르는 연결을 적으면 알린다(오타일 수 있다).
 */
export function validateRows(
  cols: BulkColumn[], rows: BulkRow[],
  existing: Array<{ name: string; folder?: string }>, connections?: string[],
): BulkIssue[] {
  const issues: BulkIssue[] = [];
  const taken = new Set(existing.map((w) => folderKey(w.name, w.folder || '')));
  const seen = new Map<string, number>();
  const conns = connections ? new Set(connections) : null;
  rows.forEach((row, i) => {
    const name = (row.name ?? '').trim();
    const folder = (row.folder ?? '').trim();
    if (!name) issues.push({ row: i, key: 'name', level: 'error', code: 'nameRequired' });
    else {
      const k = folderKey(name, folder);
      if (taken.has(k)) issues.push({ row: i, key: 'name', level: 'error', code: 'nameExists', params: { name } });
      if (seen.has(k)) issues.push({ row: i, key: 'name', level: 'error', code: 'nameDupRows', params: { name, other: (seen.get(k) as number) + 1 } });
      else seen.set(k, i);
    }
    if (row.schedule !== undefined && !cronOk(row.schedule.trim())) issues.push({ row: i, key: 'schedule', level: 'error', code: 'badCron', params: { value: row.schedule } });
    cols.forEach((c) => {
      const raw = row[c.key];
      if (raw === undefined) return;
      if (c.kind === 'cfg' && c.valueType === 'number' && raw.trim() !== '' && !Number.isFinite(Number(raw))) {
        issues.push({ row: i, key: c.key, level: 'error', code: 'notNumber', params: { value: raw } });
      }
      if (c.kind === 'cfg' && c.valueType === 'boolean' && parseBool(raw) === null) {
        issues.push({ row: i, key: c.key, level: 'error', code: 'notBoolean', params: { value: raw } });
      }
      if (c.kind === 'var' && raw.trim() === '') {
        issues.push({ row: i, key: c.key, level: 'warning', code: 'unresolvedVar', params: { name: c.field } });
      }
      if (conns && c.kind === 'cfg' && CONN_KEYS.test(c.field) && raw.trim() && !conns.has(raw.trim())) {
        issues.push({ row: i, key: c.key, level: 'warning', code: 'unknownConn', params: { value: raw } });
      }
    });
  });
  return issues;
}

// ---------------------------------------------------------------- CSV

const csvCell = (v: string) => (/[",\r\n]/.test(v) || /^\s|\s$/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

/** 표를 CSV 로 — 엑셀이 한글을 깨지 않게 BOM 을 앞에 붙인다 */
export function toCsv(keys: string[], rows: BulkRow[]): string {
  const lines = [keys.map(csvCell).join(',')];
  rows.forEach((r) => lines.push(keys.map((k) => csvCell(r[k] ?? '')).join(',')));
  return `﻿${lines.join('\r\n')}\r\n`;
}

/** CSV 글 → 칸 배열 (따옴표 안의 쉼표·줄바꿈·"" 를 안다) */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^﻿/, '');
  const out: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i += 1; } else quoted = false;
      } else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i += 1;
      row.push(cell); out.push(row); row = []; cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) { row.push(cell); out.push(row); }
  return out.filter((r) => r.some((c) => c !== ''));
}

export interface CsvReadResult {
  rows: BulkRow[];
  /** 파일에 있던 열 중 이 템플릿의 열 키 */
  keys: string[];
  /** 템플릿에 없는 머리글 — 다른 템플릿으로 만든 파일일 수 있다 */
  unknown: string[];
}

/** CSV 를 표 행으로 — 파일에 없는 열은 템플릿 값으로 채운다 */
export function rowsFromCsv(cols: BulkColumn[], text: string): CsvReadResult {
  const table = parseCsv(text);
  if (!table.length) return { rows: [], keys: [], unknown: [] };
  const header = table[0].map((h) => h.trim());
  const known = new Map(cols.map((c) => [c.key, c]));
  const keys = header.filter((h) => known.has(h));
  const unknown = header.filter((h) => h && !known.has(h));
  const rows = table.slice(1).map((cells) => {
    const r: BulkRow = {};
    cols.forEach((c) => { r[c.key] = c.templateValue; });
    header.forEach((h, j) => { if (known.has(h)) r[h] = cells[j] ?? ''; });
    return r;
  });
  return { rows, keys, unknown };
}
